/**
 * Single source of truth for plan numbers and the sentences built from them.
 *
 * WHY THIS EXISTS
 * ---------------
 * "15 scans a month" was written out by hand in 13 places and "1 free scan" in
 * 10, across components that each read a different field to decide which to
 * show. They drifted: `pages/auth/Signup.jsx` promised "10 free scans" while
 * every other surface said one, and several components appended "today" to a
 * counter that was actually the month's. Every one of those is the app telling
 * a user something untrue about their own account.
 *
 * The rule now: no component writes a plan number or a period word itself. It
 * calls a helper here.
 *
 * SERVER IS AUTHORITATIVE
 * -----------------------
 * The real catalogue lives in `backend/plans_catalog.py` and is served by
 * `GET /api/billing/plans`. `hydrateFromCatalog()` lets whoever fetches it push
 * the true numbers in, so a pricing change on the server propagates without a
 * frontend edit. The constants below are only the pre-fetch fallback; they must
 * stay in sync with `plans_catalog.py` and are marked at their definition.
 *
 * PERIOD WORDS
 * ------------
 * `periodLabel()` returns null when the plan model is unknown rather than
 * guessing "today". A caller that gets null must render no period word at all —
 * see the three-states law: unknown is not the same as daily.
 */

// Fallbacks — MUST match backend/plans_catalog.py SUBSCRIPTION_PLANS.starter_monthly
// and the free-trial policy enforced in backend/entitlements.py.
let STARTER_SCANS_PER_MONTH = 15;
let STARTER_KEYWORDS_PER_SCAN = 15;
let FREE_SCANS = 1; // entitlements.py `_scan_used`: ONE lifetime Find-Customers scan

/** Push the server catalogue in. Safe to call repeatedly; ignores junk. */
export function hydrateFromCatalog(catalog) {
  const starter = catalog?.subscriptions?.find((s) => s.key === "starter_monthly");
  if (!starter) return;
  if (Number.isFinite(starter.scans_per_month)) {
    STARTER_SCANS_PER_MONTH = starter.scans_per_month;
  }
  if (Number.isFinite(starter.keywords_per_scan)) {
    STARTER_KEYWORDS_PER_SCAN = starter.keywords_per_scan;
  }
}

export const planNumbers = () => ({
  starterScans: STARTER_SCANS_PER_MONTH,
  starterKeywords: STARTER_KEYWORDS_PER_SCAN,
  freeScans: FREE_SCANS,
});

// ── Phrases ────────────────────────────────────────────────────────────────

/** "15 scans a month" — the upgrade pitch fragment. */
export const starterScansPhrase = () => `${STARTER_SCANS_PER_MONTH} scans a month`;

/** Full upgrade pitch used wherever we ask a free user to buy. */
export const starterPitch = () =>
  `Upgrade to Starter for ${starterScansPhrase()}, plus open-source links and saved leads.`;

/** "1 free scan" / "your first scan is free" — the trial, stated once. */
export const freeScansPhrase = () =>
  FREE_SCANS === 1 ? "1 free scan" : `${FREE_SCANS} free scans`;

export const freeTrialBlurb = () =>
  FREE_SCANS === 1
    ? "Your first scan is free."
    : `Your first ${FREE_SCANS} scans are free.`;

/** Shown once the trial is spent. */
export const freeScanUsedPhrase = () =>
  `Free scan used — upgrade for ${starterScansPhrase()}.`;

// ── Period vocabulary, driven by the server's quota model ──────────────────

/**
 * "this month" | "today" | null.
 *
 * null means we do not know the model — the caller must omit the period word
 * rather than substitute one. On a monthly plan `account_used`/`account_limit`
 * are back-compat ALIASES for the monthly allowance, so calling them "today" is
 * simply wrong; that mislabel is the reason this helper exists.
 */
export function periodLabel(usage) {
  const model = usage?.quota_model;
  if (model === "monthly") return "this month";
  if (model === "daily") return "today";
  return null;
}

/** "Scans this month" | "Scans today" | "Scans" — never a wrong period word. */
export function scansCounterLabel(usage) {
  const p = periodLabel(usage);
  return p ? `Scans ${p}` : "Scans";
}

/**
 * How many scans are left, as a three-state result.
 *
 *   { known: false }                       → render nothing numeric
 *   { known: true, remaining, period }     → safe to display
 *
 * Never defaults to 0. "You have 0 scans left" rendered during a fetch gap is
 * indistinguishable from a genuinely empty account, and it appears exactly when
 * the user is about to act on it.
 */
export function scansRemaining(usage) {
  if (!usage) return { known: false };
  const model = usage.quota_model;
  if (model === "monthly") {
    const bal = usage.allowance_balance;
    if (!Number.isFinite(bal)) return { known: false };
    return { known: true, remaining: bal, period: "this month" };
  }
  const rem = usage.account_remaining;
  if (!Number.isFinite(rem)) return { known: false };
  return {
    known: true,
    remaining: rem,
    period: model === "daily" ? "today" : null,
  };
}

/** "You have 3 scans left this month" — or null when we cannot say honestly. */
export function scansLeftSentence(usage) {
  const r = scansRemaining(usage);
  if (!r.known) return null;
  const n = r.remaining;
  const noun = n === 1 ? "scan" : "scans";
  return r.period
    ? `You have ${n} ${noun} left ${r.period}`
    : `You have ${n} ${noun} left`;
}
